/** Rate comparison table component with side-by-side carrier costs. */
import React, { useMemo, useState, useEffect, useCallback } from "react";
import apiClient, { API_BASE_URL } from "../../api/client";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  flexRender,
} from "@tanstack/react-table";

/** Carrier comparison interface from API. */
export interface CarrierComparison {
  supplier_id: string;
  supplier_name: string;
  destination_prefix: string;
  carrier_cost: number;
  margin_amount: number;
  effective_cost: number;
  margin_percent?: number;
  margin_floor?: number;
}

/** Rate comparison response interface. */
export interface RateComparisonResponse {
  destination: string;
  tenant: string;
  reseller_subject?: string;
  carriers: CarrierComparison[];
  total_carriers: number;
  lowest_cost_index: number;
  timestamp: string;
}

/** Props for RateComparisonTable. */
interface RateComparisonTableProps {
  /** Destination number to compare */
  destination: string;
  /** Optional reseller subject for margin breakdown */
  resellerSubject?: string;
  /** Clear comparison callback */
  onClear?: () => void;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<CarrierComparison>();

/** Format number as currency. */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(value);
};

/** Rate Comparison Table component. */
export default function RateComparisonTable({
  destination,
  resellerSubject,
  onClear,
}: RateComparisonTableProps) {
  const [comparison, setComparison] = useState<RateComparisonResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "effective_cost", desc: false }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [lcrDialogOpen, setLcrDialogOpen] = useState(false);
  const [lcrStrategy, setLcrStrategy] = useState<string>("*lowest_cost");
  const [selectedCarriers, setSelectedCarriers] = useState<Set<string>>(new Set());
  const [lcrPriority, setLcrPriority] = useState<number>(100);
  const [creatingRule, setCreatingRule] = useState(false);

  /** Fetch rate comparison from API. */
  const fetchComparison = useCallback(async () => {
    if (!destination) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        destination: destination,
        tenant: "b1",
      });

      if (resellerSubject) {
        params.append("reseller_subject", resellerSubject);
      }

      const response = await apiClient.get<RateComparisonResponse>(
        `${API_BASE_URL}/api/v1/rates/compare?${params.toString()}`
      );

      const data: RateComparisonResponse = response.data;
      setComparison(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
      setComparison(null);
    } finally {
      setLoading(false);
    }
  }, [destination, resellerSubject]);

  /** Load comparison on mount or when destination/reseller changes. */
  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  /** Export comparison to CSV. */
  const handleExportCSV = useCallback(() => {
    if (!comparison) return;

    const headers = [
      "Carrier Name",
      "Destination Prefix",
      "Carrier Cost",
      "Margin Amount",
      "Effective Cost",
      "Margin %",
    ];

    const rows = comparison.carriers.map((carrier, index) => [
      carrier.supplier_name,
      carrier.destination_prefix,
      carrier.carrier_cost.toFixed(4),
      carrier.margin_amount.toFixed(4),
      carrier.effective_cost.toFixed(4),
      carrier.margin_percent?.toFixed(2) || "",
      index === comparison.lowest_cost_index ? "*" : "",
    ]);

    const csvContent = [
      "# Rate Comparison",
      `# Destination: ${comparison.destination}`,
      `# Generated: ${comparison.timestamp}`,
      `# Total Carriers: ${comparison.total_carriers}`,
      "",
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rate-comparison-${comparison.destination}-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [comparison]);

  /** Open LCR rule creation dialog with pre-filled values. */
  const handleOpenLCRDialog = useCallback(() => {
    if (!comparison) return;

    // Pre-select all carriers
    const allCarrierIds = new Set(comparison.carriers.map((c) => c.supplier_id));
    setSelectedCarriers(allCarrierIds);
    setLcrStrategy("*lowest_cost");
    setLcrPriority(100);
    setLcrDialogOpen(true);
  }, [comparison]);

  /** Close LCR dialog. */
  const handleCloseLCRDialog = useCallback(() => {
    setLcrDialogOpen(false);
    setSelectedCarriers(new Set());
  }, []);

  /** Toggle carrier selection. */
  const handleToggleCarrier = useCallback((supplierId: string) => {
    setSelectedCarriers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(supplierId)) {
        newSet.delete(supplierId);
      } else {
        newSet.add(supplierId);
      }
      return newSet;
    });
  }, []);

  /** Create LCR rule from comparison. */
  const handleCreateLCRRule = useCallback(async () => {
    if (!comparison || selectedCarriers.size === 0) return;

    setCreatingRule(true);
    try {
      // Build carriers array in priority order (sorted by effective_cost)
      const carriers = comparison.carriers
        .filter((c) => selectedCarriers.has(c.supplier_id))
        .map((c) => ({
          supplier_id: c.supplier_id,
          supplier_name: c.supplier_name,
          priority: comparison.carriers.indexOf(c) + 1,
        }));

      const ruleData = {
        tenant: comparison.tenant,
        subject: "*any",
        destination: comparison.destination,
        strategy: lcrStrategy,
        carriers: carriers,
        priority: lcrPriority,
      };

      // CSRF + auth headers: added automatically by the apiClient interceptors
      await apiClient.post(`${API_BASE_URL}/api/v1/lcr/rules`, ruleData);

      // Show success message
      alert("LCR rule created successfully! Redirecting to LCR rules page...");

      // Redirect to LCR rules page
      window.location.href = "/lcr";
    } catch (err) {
      alert(err instanceof Error ? err.message : "Unknown error creating LCR rule");
    } finally {
      setCreatingRule(false);
    }
  }, [comparison, selectedCarriers, lcrStrategy, lcrPriority]);

  /** Table columns definition. */
  const columns = useMemo(
    () => [
      columnHelper.accessor("supplier_name", {
        id: "supplier_name",
        header: "Carrier Name",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("destination_prefix", {
        id: "destination_prefix",
        header: "Destination Prefix",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("carrier_cost", {
        id: "carrier_cost",
        header: "Carrier Cost",
        cell: (info) => formatCurrency(info.getValue()),
      }),
      columnHelper.accessor("margin_amount", {
        id: "margin_amount",
        header: "Margin Amount",
        cell: (info) => {
          const value = info.getValue();
          return value > 0 ? formatCurrency(value) : "-";
        },
      }),
      columnHelper.accessor("effective_cost", {
        id: "effective_cost",
        header: "Effective Cost",
        cell: (info) => formatCurrency(info.getValue()),
      }),
      columnHelper.accessor("margin_percent", {
        id: "margin_percent",
        header: "Margin %",
        cell: (info) => {
          const value = info.getValue();
          return value ? `${value.toFixed(2)}%` : "-";
        },
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        cell: (props) => {
          const rowIndex = props.row.index;
          if (comparison && rowIndex === comparison.lowest_cost_index) {
            return (
              <Chip
                icon={<CheckCircleIcon />}
                label="Lowest Cost"
                color="success"
                size="small"
              />
            );
          }
          return null;
        },
      }),
    ],
    [comparison]
  );

  /** TanStack Table instance. */
  const table = useReactTable({
    data: comparison?.carriers || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!comparison || comparison.carriers.length === 0) {
    return (
      <Alert severity="info">
        No carriers found for destination {destination}. Please check the destination number and try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Card */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              Comparison Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Carriers: {comparison.total_carriers} | Lowest Cost:{" "}
              {formatCurrency(comparison.carriers[comparison.lowest_cost_index].effective_cost)} (
              {comparison.carriers[comparison.lowest_cost_index].supplier_name})
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenLCRDialog}
              color="primary"
            >
              Create Routing Rule
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
            >
              Export CSV
            </Button>
            {onClear && (
              <Button variant="outlined" onClick={onClear}>
                Clear
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Filter Input */}
      <Box mb={2}>
        <TextField
          fullWidth
          placeholder="Filter by carrier name..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          size="small"
        />
      </Box>

      {/* Comparison Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id}>
                    {header.column.getCanSort() ? (
                      <TableSortLabel
                        active={!!header.column.getIsSorted()}
                        direction={header.column.getIsSorted() === "asc" ? "asc" : "desc"}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const isLowestCost = row.index === comparison.lowest_cost_index;
              return (
                <TableRow
                  key={row.id}
                  sx={{
                    backgroundColor: isLowestCost ? "success.light" : "inherit",
                    "&:hover": {
                      backgroundColor: isLowestCost ? "success.main" : "action.hover",
                    },
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* LCR Rule Creation Dialog */}
      <Dialog open={lcrDialogOpen} onClose={handleCloseLCRDialog} maxWidth="md" fullWidth>
        <DialogTitle>Create LCR Rule from Comparison</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
            {/* Destination (read-only) */}
            <TextField
              fullWidth
              label="Destination"
              value={comparison?.destination || ""}
              disabled
              helperText="Destination from rate comparison"
            />

            {/* Strategy Select */}
            <FormControl fullWidth>
              <InputLabel>Strategy</InputLabel>
              <Select
                value={lcrStrategy}
                label="Strategy"
                onChange={(e) => setLcrStrategy(e.target.value)}
              >
                <MenuItem value="*lowest_cost">*lowest_cost</MenuItem>
                <MenuItem value="*static">*static</MenuItem>
                <MenuItem value="*weight">*weight</MenuItem>
              </Select>
            </FormControl>

            {/* Priority Input */}
            <TextField
              fullWidth
              type="number"
              label="Priority"
              value={lcrPriority}
              onChange={(e) => setLcrPriority(parseInt(e.target.value) || 100)}
              helperText="Lower values = higher priority (default: 100)"
            />

            {/* Carriers List */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Carriers (in priority order):
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: "auto" }}>
                {comparison?.carriers.map((carrier, index) => (
                  <MenuItem
                    key={carrier.supplier_id}
                    dense
                    onClick={() => handleToggleCarrier(carrier.supplier_id)}
                  >
                    <Checkbox
                      checked={selectedCarriers.has(carrier.supplier_id)}
                      size="small"
                    />
                    <ListItemText
                      primary={`${index + 1}. ${carrier.supplier_name}`}
                      secondary={`Cost: ${formatCurrency(carrier.effective_cost)}`}
                    />
                  </MenuItem>
                ))}
              </Paper>
              <Typography variant="caption" color="text.secondary">
                {selectedCarriers.size} carriers selected
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLCRDialog} disabled={creatingRule}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateLCRRule}
            variant="contained"
            disabled={creatingRule || selectedCarriers.size === 0}
            startIcon={creatingRule ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creatingRule ? "Creating..." : "Create Rule"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
